/* eslint-disable @typescript-eslint/no-explicit-any */
/********************************
 * Global State Definitions
 ******************************** */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { api } from 'lib/api';
import { ModuleKeyType } from 'lib/api/company/types';

import { useOnCompanyChangeCAResetAtoms } from './Admin/Dashboard/CADashboard/hooks';
import { useOnCompanyChangeEpResetAtoms } from './Admin/Dashboard/EPDashboard/hooks';
import { useOnCompanyChangePCResetAtoms } from './Admin/Dashboard/PCDashboard/hooks';
import { useOnCompanyChangeSast2ResetAtoms } from './Admin/Dashboard/SASTDashboard2/hooks';
import { ChartType, DashboardType } from './Admin/Dashboard/types';
import { useOnCompanyChangeWaResetAtoms } from './Admin/Dashboard/WADashboard/hooks';
import { useOnCompanyChangeFindingsResetAtoms } from './Findings/state';
import { useOnCompanyChangeMembersConfigResetAtoms } from './Members/state';
import { useOnCompanyChangePCFindingsResetAtoms } from './PCFindings/state';
import { useOnCompanyChangeActivationTabConfigResetAtoms } from './Company/Configuration/tabs/CAActivationKeysTab/state';
import { useOnCompanyChangeIgnoredQIDTabConfigResetAtoms } from './Company/Configuration/tabs/IgnoredQIDsConfigTab/state';

export const companyDataAtom = atom(async () => {
  try {
    const responseData = await api.company.getAllCompanies({
      limit: 550,
      ordering: 'display_name',
    });
    return responseData?.results;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return [];
});

const _baseCompanyIdAtom = atomWithStorage<number>('currentCompanyId', -1);
export const currentCompanyAtom = atomWithStorage<string>('currentCompany', '');
export const companyLogoAtom = atomWithStorage<string>('companyLogo', '');

export const companyIdAtom = atom(
  async (get) => {
    const baseCompanyId = get(_baseCompanyIdAtom);
    const companyData = get(companyDataAtom);

    if (baseCompanyId !== -1) return baseCompanyId;

    const firstCompanyId =
      companyData && companyData[0] ? companyData[0].id : -1;
    return firstCompanyId;
  },
  (get, set, newValue: number) => {
    set(_baseCompanyIdAtom, newValue);
  },
);

// export const currentDashboard = atom(Dashboard.WAS)
export const currentDashboardAtom = atomWithStorage(
  'dashboard_type',
  DashboardType.CA,
);
export const currentChartTypeAtom = atomWithStorage<ChartType>(
  'dashboard_chart_type',
  ChartType.Bar,
);
export type HostActiveStatusFilterType = 'active' | 'aging' | 'deleted';

export const enabledModulesAtom = atom<ModuleKeyType[]>((get) => {
  const companyId = get(companyIdAtom);
  const companies = get(companyDataAtom);

  if (companyId <= 0 || !companies || companies.length === 0) return [];

  // Find the current company in the already loaded company data
  const currentCompany = companies.find((company) => company.id === companyId);

  // Return the enabled modules for the current company
  return currentCompany?.enabled_modules || [];
});

export function useOnCompanyChangeResetAtoms() {
  const resetCaAtoms = useOnCompanyChangeCAResetAtoms();
  const resetEpAtoms = useOnCompanyChangeEpResetAtoms();
  const resetWaAtoms = useOnCompanyChangeWaResetAtoms();
  const resetSast2Atoms = useOnCompanyChangeSast2ResetAtoms();

  // finding listing
  const resetFindingListingAtoms = useOnCompanyChangeFindingsResetAtoms();

  // reset PC findings atoms
  const resetPCFindingsAtoms = useOnCompanyChangePCFindingsResetAtoms();
  const resetPCDashboardAtoms = useOnCompanyChangePCResetAtoms();

  // members config
  const resetMembersConfigAtoms = useOnCompanyChangeMembersConfigResetAtoms();

  // Activation config
  const resetActivationConfigAtoms =
    useOnCompanyChangeActivationTabConfigResetAtoms();

  // Ignore QID config
  const resetIgnoreQIDAtoms = useOnCompanyChangeIgnoredQIDTabConfigResetAtoms();
  const resetHosts = () => {
    resetCaAtoms();
    resetEpAtoms();
    resetWaAtoms();
    resetSast2Atoms();
    resetFindingListingAtoms();
    resetPCFindingsAtoms();
    resetPCDashboardAtoms();
    resetMembersConfigAtoms();
    resetActivationConfigAtoms();
    resetIgnoreQIDAtoms();
  };

  return resetHosts;
}
